import type { GameType } from '@ottd/api';

import { tr } from '@/lib/locale';

/**
 * The two server-type visual themes ported from the prototype (Homepage,
 * Servers, Games, Game). CB = City Builder (amber), CV = Company Value (blue).
 * Bevel border colors are exact prototype hexes applied via inline style.
 */
export interface ServerTypeTheme {
  /** Two-letter type label shown in the square tile. */
  mono: string;
  /** Long label, e.g. "City Builder", in the active language. */
  label: string;
  /** Tile letter color. */
  monoColor: string;
  /** Tile two-tone bevel (top/right/bottom/left). */
  monoBorder: string;
  /** Secondary text over the image banner. */
  subColor: string;
  /** Link color over the image banner. */
  linkColor: string;
  /** Left→right tint gradient over the banner image. */
  tint: string;
}

/** The palette half of a theme; the label is resolved per call. */
type Palette = Omit<ServerTypeTheme, 'label'>;

const CB: Palette = {
  mono: 'CB',
  monoColor: '#ffb61e',
  monoBorder: '#ffb968 #6e3c05 #6e3c05 #ffb968',
  subColor: '#ffe2b8',
  linkColor: '#ffd34d',
  tint: 'linear-gradient(90deg, rgba(199,120,23,.78), rgba(20,25,33,.55))',
};

const CV: Palette = {
  mono: 'CV',
  monoColor: '#7fa8d9',
  monoBorder: '#7fa8d9 #16304c #16304c #7fa8d9',
  subColor: '#c8ddf2',
  linkColor: '#a8d4ff',
  tint: 'linear-gradient(90deg, rgba(44,87,132,.8), rgba(20,25,33,.55))',
};

const NEUTRAL: Palette = {
  mono: '??',
  monoColor: '#98a2b0',
  monoBorder: '#7c8caa #10141c #10141c #7c8caa',
  subColor: '#d8dde5',
  linkColor: '#8ed065',
  tint: 'linear-gradient(90deg, rgba(20,25,33,.8), rgba(20,25,33,.55))',
};

/** Resolves the theme for a `CB`/`CV` (or unknown) server/game type. */
export function serverTypeTheme(type: GameType | null | undefined): ServerTypeTheme {
  if (type === 'CB') return { ...CB, label: tr('common:serverType.CB') };
  if (type === 'CV') return { ...CV, label: tr('common:serverType.CV') };
  return { ...NEUTRAL, label: tr('common:serverType.unknown') };
}
